import React from 'react';
import {ApolloProvider} from '@apollo/client';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import AlertError from '../../common/alert/AlertError';
import Alert from "react-bootstrap/Alert";

import Facets from '../../components/search/facets/Facets';
import SearchToolbar from '../../components/search/toolbar/SearchToolbar';
import SearchField, {SearchFieldOption} from '../../components/search/search-field/SearchField';
import {
  FacetsList,
  FilterSelections,
  Results,
  SearchObjectsModel,
  SearchQuery,
  SearchTerms,
  FilterSelection,
  SearchArguments,
  SearchResponse
} from '../../types/Search';
import SearchResults from '../../components/search/results/SearchResults';
import SelectedFilters from "../../components/search/facets/selected-filters/SelectedFilters";
import SearchResponseConverter from "./utils/search-response-converter";
import * as QueryFactory from "./graphql/query-factory";
import SearchService from '../../services/search-service';

import {SchemaContext} from "../../components/schema/context/SchemaContext";
import GraphQLService from "../../services/graphql-service";
import SearchUtils from "./utils/search-utils";
import Loader from '../../components/loader/Loader';
import isEqual from 'lodash/isEqual';

type SearchState = {
  // The search terms to be used in search searched
  searchTerms: SearchTerms;
  // The starting index from which next batch result to be returned
  from: number;
  // The page size
  size: number;
  // Currently active/selected page
  currentPage: number;
  // The search results model
  results: Results;
  // The facets model
  facets: FacetsList;
  // Holds selection from faceted filters mapped by composite key e.g. 'keyword-Human-eyeColor'
  filterSelection: FilterSelections;
  // The request progress status
  loading: boolean;
  error: Array<string>;
  hasSearched: boolean;
  // Holds information about the searchable objects and their properties retrieved via Search Service GQL introspection
  searchObjectsModel: SearchObjectsModel;
  // Keeps track if the search component had fully been initialized
  initialized: boolean;
};

/**
 * A page exposing the search capabilities of the Platform's search service.
 * This is a smart component which maintains state and provides it to related components.
 */
class Search extends React.Component<{}, SearchState> {

  static contextType = SchemaContext;

  private readonly searchService: SearchService;
  // Initial index from which results to be returned
  private readonly INITIAL_SEARCH_INDEX: number = 0;
  // Initial page number to be selected
  private readonly INITIAL_SELECTED_PAGE: number = 1;
  // Default page size
  private readonly DEFAULT_PAGE_SIZE: number = 10;

  constructor(props: {}) {
    super(props);
    this.state = this.getDefaultSearchState({} as SearchObjectsModel);
    this.searchService = new SearchService();
  }

  /**
   * Returns the search state with default values
   */
  getDefaultSearchState = (searchObjectsModel: SearchObjectsModel): SearchState => {
    return {
      searchTerms: {freeText: [], concept: undefined, filters: {}},
      from: this.INITIAL_SEARCH_INDEX,
      size: this.DEFAULT_PAGE_SIZE,
      currentPage: this.INITIAL_SELECTED_PAGE,
      results: {
        total: 0,
        count: 0,
        columns: [
          {label: 'Name', value: 'name'},
          {label: 'Type', value: 'type'},
          {label: 'ID', value: 'id'},
          {label: 'Index', value: 'index'}
        ],
        resultItems: []
      },
      facets: [],
      filterSelection: {},
      loading: false,
      error: [],
      hasSearched: false,
      searchObjectsModel,
      initialized: true
    };
  };

  /**
   * Loads the search GraphQlSchema which will be used in the page components.
   */
  componentDidMount() {
    this.setState({initialized: false});
    GraphQLService.executeSchemaIntrospection('search')
      .then(searchGQLSchema => this.setState({searchObjectsModel: SearchUtils.buildSearchObjectsModel(searchGQLSchema)}))
      .catch(errorResponse => this.handleErrors(errorResponse))
      .finally(() => this.setState({initialized: true}));
  }

  // event handlers

  /**
   * Handles the search event triggered by search field component.
   * If there is no concept or free text search terms, the search state
   * is reset to initial state
   * @param {SearchTerms} searchTerms The new search terms.
   */
  onSearch = (searchTerms: SearchTerms): void => {
    if (!searchTerms.concept?.id && !searchTerms.freeText.length) {
      this.setState(this.getDefaultSearchState(this.state.searchObjectsModel));
      return;
    }
    this.search({
      searchTerms,
      from: this.INITIAL_SEARCH_INDEX,
      size: this.state.size,
      currentPage: this.INITIAL_SELECTED_PAGE,
      filterSelection: {}
    });
  };

  /**
   * Handles changes in the pagination criteria.
   * @param {number} page The current page.
   */
  onPage = (page: number): void => {
    const nextPage = this.state.size * (page - 1);
    this.search({
      searchTerms: this.state.searchTerms,
      from: nextPage,
      size: this.state.size,
      currentPage: page,
      filterSelection: this.state.filterSelection
    },
      false);
  };

  /**
   * Handles the change in the page size.
   * @param {number} size The new page size value.
   */
  onPageSizeChange = (size: number): void => {
    // Set the new size and also reset the starting position to prevent locking in
    // page without possibility to get back to previous pages if current page was not
    // the first one and page size is greater than total amount of pages.
    this.search({
      searchTerms: this.state.searchTerms,
      size,
      from: this.INITIAL_SEARCH_INDEX,
      currentPage: this.INITIAL_SELECTED_PAGE,
      filterSelection: this.state.filterSelection
    },
      false);
  };

  /**
   * Handles autocomplete field value changes and fetches suggestions.
   * @param {string} inputValue The actual string value entered in the search field
   * @param {(options: Array<SelectOption>) => void} loadOptions A callback that must be called to
   * populate suggestions based on the inputValue.
   */
  fetchSuggestion = (inputValue: string, loadOptions: (options: Array<SearchFieldOption>) => void) => {
    const searchTerms = inputValue.trim();
    const suggestQuery: SearchQuery = QueryFactory.getSuggestQuery(searchTerms, this.state.searchObjectsModel);
    this.executeQuery(suggestQuery)
      .then((response: SearchResponse) => {
        const suggestedOptions: Array<SearchFieldOption> = [];
        suggestedOptions.push({
          value: inputValue,
          label: `Free text: ${inputValue}`
        });
        SearchResponseConverter.convertAutoSuggestResponseToModel(response, suggestedOptions);
        loadOptions(suggestedOptions);
      })
      .catch((errorResponse: any) => this.handleErrors(errorResponse));
  };

  /**
   * Handles facet selections and executes filtering.
   * @param {FilterSelection} selected
   */
  onFilter = (selected: FilterSelection) => {
    const updatedSelections = {...this.state.filterSelection};
    // checks if the client selects already selected filter and skips the execution of the search request
    if (isEqual(selected.value, updatedSelections[selected.key]?.value)) {
      return;
    }
    updatedSelections[selected.key] = selected;
    const searchTerms = {...this.state.searchTerms};
    searchTerms.filters = updatedSelections;
    this.search({
      searchTerms,
      size: this.state.size,
      from: this.INITIAL_SEARCH_INDEX,
      currentPage: this.INITIAL_SELECTED_PAGE,
      filterSelection: updatedSelections
    });
  };

  /**
   * Handles deselection of selected filter.
   * @param {string} deselected The key of deselected filter
   */
  onFilterDeselect = (deselected: Array<string>) => {
    const selections = this.state.filterSelection;
    const updatedSelections: FilterSelections = {};
    Object.keys(selections).forEach((key: string) => {
      if (deselected.indexOf(key) < 0) {
        updatedSelections[key] = selections[key];
      }
    });
    const searchTerms = {...this.state.searchTerms};
    searchTerms.filters = updatedSelections;
    this.search({
      searchTerms,
      size: this.state.size,
      from: this.INITIAL_SEARCH_INDEX,
      currentPage: this.INITIAL_SELECTED_PAGE,
      filterSelection: updatedSelections
    });
  };

  /**
   * Deselects a single filter.
   *
   * @param deselected the deselected filter's key
   */
  onSingleFilterDeselect = (deselected: string) => {
    this.onFilterDeselect([deselected]);
  }

  // privates

  /**
   * Performs the search using provided arguments.
   * The state is updated with the properties passed with the args payload before the search.
   *
   * @param {SearchArguments} args
   * @param {boolean} includeAggregations flag which controls the building of the aggregation
   * part of the search query. Default value: <code>true</code>.
   */
  private search(args: SearchArguments, includeAggregations = true): void {
    this.setState({
      searchTerms: args.searchTerms,
      size: args.size,
      from: args.from,
      currentPage: args.currentPage,
      filterSelection: args.filterSelection,
      loading: true
    });
    const searchQuery = QueryFactory.getSearchQuery(args, this.state.searchObjectsModel, includeAggregations);
    this.executeQuery(searchQuery)
      .then((response: SearchResponse) => {
        const results: Results = {...this.state.results, resultItems: []};
        SearchResponseConverter.buildSearchResultsModel(response, results);
        const facets = this.buildFacets(response, args, includeAggregations);
        this.setState({results, facets});
      })
      .catch((errorResponse: any) => this.handleErrors(errorResponse))
      .finally(() => this.setState({loading: false, hasSearched: true}));
  }

  private executeQuery(query: SearchQuery): Promise<SearchResponse> {
    return this.searchService.query(query)
      .then(Search.handleGraphqlErrors)
      .then((response: any) => response.data as SearchResponse);
  }

  private buildFacets(response: SearchResponse, args: SearchArguments, includeAggregations: boolean): FacetsList {
    // calculate/recalculates the facets, if the aggregations are included, otherwise keep the facets unchanged
    if (includeAggregations) {
      const facets: FacetsList = [];
      SearchResponseConverter.buildFacetsList(response, facets, args.filterSelection);
      return facets;
    }
    return this.state.facets;
  }

  private handleErrors(error: any): void {
    console.error('[Error]:', error);
    this.setState({error: error});
  }

  private static handleGraphqlErrors(response: any) {
    // This usually should not happen in production. If it does, then probably there is a
    // change in the graphql schema that makes search queries invalid.
    if (response.errors) {
      return Promise.reject(['[GraphQL errors]:', ...response.errors]);
    }
    return response;
  }

  render() {
    if (!this.state.initialized) {
      return <InitializeLoader />;
    }

    if (this.state.error.length) {
      return <ErrorMessages errors={this.state.error}/>;
    }

    const hasSelection = Object.keys(this.state.filterSelection).length;
    return (
      <ApolloProvider client={this.searchService.getClient()}>
        <Container fluid className="SearchPageContent no-padding">
          <Row className="mb-3">
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
              <SearchField
                searchTerms={this.state.searchTerms}
                onSearch={this.onSearch}
                onFetchSuggestions={this.fetchSuggestion}
                disabled={this.state.loading}
              />
            </Col>
          </Row>
          <NoResultsMessage hasSearched={this.state.hasSearched} results={this.state.results}/>
          <Row>
            <Col xl={2} lg={2} md={0} sm={0} xs={0}>
              <Facets facets={this.state.facets}
                      onSelect={this.onFilter}
                      onDeselect={this.onSingleFilterDeselect}/>
            </Col>
            <Col xl={10} lg={10} md={12} sm={12} xs={12}>
              {hasSelection ?
                <Row>
                  <Col>
                    <SelectedFilters className="pb-3 mb-2 border-bottom"
                                     selection={this.state.filterSelection}
                                     onDeselect={this.onFilterDeselect}/>
                  </Col>
                </Row>
                : null
              }
              <Row>
                <Col>
                  <SearchToolbar className="pb-2"
                                 disabled={this.state.loading}
                                 data={this.state.results}
                                 from={this.state.from}
                                 onPageSizeChange={this.onPageSizeChange}/>
                </Col>
              </Row>
              <Row>
                <Col>
                  <SearchResults
                    data={this.state.results}
                    currentPage={this.state.currentPage}
                    pageSize={this.state.size}
                    onPage={this.onPage}
                    disabled={this.state.loading}
                    graphQlSchema={this.context.graphQlSchema}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </ApolloProvider>
    );
  }
}

function InitializeLoader() {
  return (
    <div className="text-center">
      <Loader block={true} size="lg"/>
      <span>Loading GraphQL schema</span>
    </div>
  );
}

function NoResultsMessage(props: { hasSearched: boolean, results: Results }) {
  if (props.hasSearched && props.results.total < 1) {
    return (
      <Alert variant="info">
        No results matching the search criteria
      </Alert>
    );
  }
  return null;
}

function ErrorMessages(props: { errors: Array<any> }) {
  return (
    <AlertError>
      <ul>
        {props.errors.map((error: any, index: number) => {
          const errorMessage = error && JSON.stringify(error);
          return <li key={index}>{errorMessage}</li>;
        })}
      </ul>
    </AlertError>
  );
}

export default Search;
